import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { settingsService } from '../../services/settingsService';

const TermsOfServicePage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTermsOfService();
  }, []);

  const loadTermsOfService = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const setting = await settingsService?.getSettingByKey('page_terms_of_service');
      
      if (setting?.settingValue) {
        setContent(setting?.settingValue);
      } else {
        // Default content if not found in database
        setContent({
          title: 'Terms of Service',
          lastUpdated: '2025-12-08',
          sections: [
            {
              title: 'Acceptance of Terms',
              content: 'By accessing and using RoleCall, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use our service.'
            },
            {
              title: 'Description of Service',
              content: 'RoleCall provides a musical theatre learning platform with interactive lessons, professional video content, and personalized learning paths. We reserve the right to modify or discontinue the service at any time.'
            },
            {
              title: 'User Accounts',
              content: 'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account.'
            },
            {
              title: 'Subscription and Payment',
              content: 'Our service is provided on a subscription basis at $9.99/month. Subscriptions automatically renew unless cancelled. All fees are non-refundable except as required by law.'
            },
            {
              title: 'User Conduct',
              content: 'You agree not to use the service for any unlawful purpose, to infringe on intellectual property rights, to transmit harmful content, or to interfere with the proper functioning of the platform.'
            },
            {
              title: 'Intellectual Property',
              content: 'All content, features, and functionality of RoleCall are owned by us or our licensors and are protected by copyright, trademark, and other intellectual property laws.'
            },
            {
              title: 'Limitation of Liability',
              content: 'To the fullest extent permitted by law, RoleCall shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the service.'
            },
            {
              title: 'Changes to Terms',
              content: 'We reserve the right to modify these terms at any time. Continued use of the service after changes constitutes acceptance of the modified terms.'
            },
            {
              title: 'Contact Information',
              content: 'For questions about these Terms of Service, please contact us through the support page or email us at legal@rolecall.app'
            }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading terms of service:', err);
      setError(err?.message || 'Failed to load terms of service');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading Terms of Service..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center p-6">
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 max-w-md">
          <Icon name="AlertTriangle" size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-white text-center mb-2">
            Unable to Load Terms of Service
          </h2>
          <p className="text-blue-100 text-center mb-6">{error}</p>
          <Button onClick={loadTermsOfService} className="w-full" iconName="RefreshCw" iconPosition="left">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Terms of Service - RoleCall</title>
        <meta name="description" content="RoleCall Terms of Service - Legal terms and conditions for using our musical theatre learning platform" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        {/* Navigation Bar */}
        <nav className="container mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AppImage 
              src="/assets/images/rolecall_logo_wide-1756786874531.png" 
              alt="RoleCall Logo - Musical Theatre Learning Platform"
              className="h-10 w-auto"
            />
          </div>
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-full px-6 py-2"
              iconName="Home"
              iconPosition="left"
            >
              Back to Home
            </Button>
          </div>
        </nav>

        {/* Content */}
        <div className="container mx-auto px-6 py-12 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 md:p-12"
          >
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
              {content?.title || 'Terms of Service'}
            </h1>
            
            {content?.lastUpdated && (
              <p className="text-blue-200 mb-8">
                Last Updated: {new Date(content?.lastUpdated)?.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}

            <div className="space-y-8">
              {content?.sections?.map((section, index) => (
                <div key={index}>
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    {section?.title}
                  </h2>
                  <div className="text-blue-100 leading-relaxed whitespace-pre-wrap">
                    {section?.content}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="py-8 bg-black bg-opacity-30 mt-12">
          <div className="container mx-auto px-6 text-center">
            <AppImage 
              src="/assets/images/rolecall_logo_wide-1756786874531.png" 
              alt="RoleCall Logo"
              className="h-8 w-auto mx-auto mb-4"
            />
            <p className="text-blue-200">
              © {new Date()?.getFullYear()} RoleCall. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TermsOfServicePage;